import api from "./api";
import type { PanelPermission } from "./access";

// One org-owner admin governed by the global superadmin.
export interface SuperadminAdmin {
  user_id: string;
  email: string;
  name: string;
  company_name?: string | null;
  is_active: boolean;
  // Shut out after too many wrong passwords; unlock via unlockLogin(email).
  sign_in_locked?: boolean;
  // Account mobile, falling back to the company profile's.
  mobile?: string | null;
  // As typed on the company profile (usually "07 Delhi").
  state?: string | null;
  // True once the superadmin has set a panel matrix for this admin; otherwise
  // the admin keeps the permissive role-based fallback (sees everything).
  governed: boolean;
  // Workspace plan: "starter" (free) | "professional" | "enterprise" (custom).
  // null when the admin has no subscription row.
  plan?: string | null;
  subscription_status?: string | null;
  trial_end?: string | null;
  subscription_end?: string | null;
  last_login_at?: string | null;
  branchID?: string | null;
  headOfficeID?: string | null;
  created_at?: string | null;
}

// Workspace plans: starter = Free (limited workspace), professional = full
// access, enterprise = Custom (panels picked per admin).
export type WorkspacePlan = "starter" | "professional" | "enterprise";

export interface AdminSubscription {
  user_id: string;
  head_office_id: string;
  plan: WorkspacePlan | null;
  status: string | null;
  employee_limit: number | null;
  trial_end?: string | null;
  requested_plan?: string | null;
  governed: boolean;
}

export interface AdminPermissions {
  user_id: string;
  governed: boolean;
  permissions: Record<string, PanelPermission>;
}

// List every admin governed by the logged-in superadmin (cross-tenant).
export const listAdmins = async (): Promise<SuperadminAdmin[]> => {
  const res = await api.get("/superadmin/admins");
  return Array.isArray(res.data) ? res.data : [];
};

export const getAdminPermissions = async (
  userId: string
): Promise<AdminPermissions> => {
  const res = await api.get(`/superadmin/admins/${userId}/permissions`);
  return res.data;
};

export const setAdminPermissions = async (
  userId: string,
  permissions: Record<string, PanelPermission>
): Promise<AdminPermissions> => {
  const res = await api.put(`/superadmin/admins/${userId}/permissions`, {
    permissions,
  });
  return res.data;
};

// Stop governing this admin — reverts them to the permissive fallback.
export const clearAdminPermissions = async (
  userId: string
): Promise<{ user_id: string; governed: boolean }> => {
  const res = await api.delete(`/superadmin/admins/${userId}/permissions`);
  return res.data;
};

export const setAdminStatus = async (
  userId: string,
  isActive: boolean
): Promise<{ user_id: string; is_active: boolean }> => {
  const res = await api.put(`/superadmin/admins/${userId}/status`, {
    is_active: isActive,
  });
  return res.data;
};

// Give an admin a temporary password (also lifts a sign-in lock and ends live
// sessions). They sign straight in with it and can change it from Profile.
export const resetAdminPassword = async (
  userId: string,
  newPassword: string
): Promise<{ user_id: string; success: boolean }> => {
  const res = await api.post(`/superadmin/admins/${userId}/reset-password`, {
    new_password: newPassword,
  });
  return res.data;
};

// Permanently delete an admin AND the workspace it owns — its employees, their
// logins, and every tenant-scoped row. There is no undo; setAdminStatus(false)
// is the reversible option. Returns what was removed.
export interface WorkspacePurgeResult {
  success: boolean;
  user_id: string;
  workspace_id: string;
  users: number;
  employees: number;
  rows: number;
  tables: Record<string, number>;
}

export const deleteAdmin = async (
  userId: string
): Promise<WorkspacePurgeResult> => {
  const res = await api.delete(`/superadmin/admins/${userId}`);
  return res.data;
};

// This admin's workspace plan/entitlement.
export const getAdminSubscription = async (
  userId: string
): Promise<AdminSubscription> => {
  const res = await api.get(`/superadmin/admins/${userId}/subscription`);
  return res.data;
};

// Move the admin's workspace onto a plan. Plan defaults (status + employee
// limit) are applied by the backend. `grantFullAccess` clears any custom panel
// matrix — used when upgrading to Professional, which means "sees everything".
// The change reaches the admin's app on their next sign-in (JWT claim).
export const setAdminPlan = async (
  userId: string,
  plan: WorkspacePlan,
  options: { grantFullAccess?: boolean; employeeLimit?: number | null } = {}
): Promise<AdminSubscription> => {
  const body: Record<string, unknown> = {
    plan,
    grant_full_access: !!options.grantFullAccess,
  };
  if (options.employeeLimit !== undefined) {
    body.employee_limit = options.employeeLimit;
  }
  const res = await api.put(`/superadmin/admins/${userId}/plan`, body);
  return res.data;
};

// ---------------------------------------------------------------------------
// Credits — the metered tier above panel access.
//
// Panels say what a workspace may OPEN; credits say how much of the paid
// products it may CONSUME. Three ceilings, one per metered product, each of
// them three-valued: null = ungoverned (per-user policies and code defaults
// alone), 0 = a real ceiling of zero, a number = the ceiling. DBMS also takes a
// negative value, meaning unlimited.
//
// Unlike the plan these take effect immediately — every ceiling is read from
// the database on the spend path, so lowering one stops the next message,
// meeting or upload. No sign-in needed.
// ---------------------------------------------------------------------------

export interface AdminCreditsDbms {
  // What hrms stores. `quota_bytes` is what the document service actually holds
  // — they differ exactly when a push failed, which `sync_error` explains.
  limit_bytes: number | null;
  quota_bytes: number | null;
  used_bytes: number | null;
  account_count: number | null;
  unlimited?: boolean;
  // False when the document service could not be reached: the DBMS ceiling
  // stored here is then NOT the one being enforced over there.
  reachable: boolean;
  error: string | null;
  synced_at: string | null;
  sync_error: string | null;
}

export interface AdminCredits {
  user_id: string;
  head_office_id: string;
  // False until a superadmin has saved any ceiling for this workspace.
  granted: boolean;
  period_start: string;
  period_end: string;
  granted_by: string | null;
  updated_at: string | null;
  // `used` is reported even when `limit` is null — spend with no ceiling is
  // exactly the fact that prompts setting one.
  lizzy: { limit: number | null; used: number | null };
  meetly: { limit: number | null; used: number | null; reserved: number | null };
  dbms: AdminCreditsDbms;
  max: { lizzy: number; meetly: number; dbms_bytes: number };
}

export const getAdminCredits = async (
  userId: string
): Promise<AdminCredits> => {
  const res = await api.get(`/superadmin/admins/${userId}/credits`);
  return res.data;
};

// Only the keys PRESENT in `changes` are written, so one product can be saved
// without restating the other two. An explicit null ungoverns that product —
// which is why this takes a partial object rather than three plain arguments.
export const setAdminCredits = async (
  userId: string,
  changes: {
    lizzy_monthly_credits?: number | null;
    meetly_monthly_credits?: number | null;
    dbms_quota_bytes?: number | null;
  }
): Promise<AdminCredits> => {
  const res = await api.put(`/superadmin/admins/${userId}/credits`, changes);
  return res.data;
};

// --- Lio issue queue -------------------------------------------------------
// Questions Lio couldn't answer, reported by the person who asked. They land
// with the superadmin and nowhere else: a gap in Lio is TeamLio's problem, not
// the reporting company's, so the queue spans every organisation and no
// customer admin has a view of it.

export type LioIssueStatus = "open" | "in_progress" | "resolved" | "dismissed";

export interface LioIssue {
  id: string;
  question: string;
  reply?: string | null;
  tool_used?: string | null;
  /** What the reporter added when they filed it. */
  note?: string | null;
  status: LioIssueStatus;
  /** The answer written back to the reporter. */
  response?: string | null;
  reporter_name: string;
  reporter_role?: string | null;
  /** Which customer hit it — headOfficeID and the company's name. */
  org_id?: string | null;
  org_name?: string | null;
  /** Everyone on it — people in the product org, not the reporting one.
   *  Always an array; empty means nobody is on it. */
  assignees: LioIssueAssignee[];
  reviewed_by_name?: string | null;
  reviewed_at?: string | null;
  created_at?: string | null;
}

/** One person currently on an issue. */
export interface LioIssueAssignee {
  user_id: string;
  name: string;
  assigned_at?: string | null;
}

/** One person the queue can be assigned to: an employee of the product org. */
export interface LioAssignee {
  user_id: string;
  name: string;
  designation?: string | null;
}

export interface LioIssueQueue {
  issues: LioIssue[];
  counts: Record<LioIssueStatus, number>;
  open_count: number;
  unassigned_count: number;
  /** The workspace nominated as the product team; null until one is picked,
   *  which is also when `assignees` is empty and the console says so. */
  product_org_id: string | null;
  product_org_name: string | null;
  assignees: LioAssignee[];
}

export const listLioIssues = async (params?: {
  status?: LioIssueStatus | "open_all";
  search?: string;
  /** A user id, or "unassigned". */
  assigned_to?: string;
}): Promise<LioIssueQueue> => {
  const { data } = await api.get("/superadmin/lio-issues", {
    params: {
      status: params?.status || undefined,
      search: params?.search || undefined,
      assigned_to: params?.assigned_to || undefined,
    },
  });
  return {
    issues: data?.issues || [],
    counts: data?.counts,
    open_count: data?.open_count ?? 0,
    unassigned_count: data?.unassigned_count ?? 0,
    product_org_id: data?.product_org_id ?? null,
    product_org_name: data?.product_org_name ?? null,
    assignees: data?.assignees || [],
  };
};

/** Change a report's status, answer the reporter, or set who is on it.
 *  `assignees` is the WHOLE set — send what the picker shows; `[]` clears it.
 *  Only the people it adds are notified. */
export const updateLioIssue = async (
  id: string,
  body: { status?: LioIssueStatus; response?: string; assignees?: string[] },
): Promise<LioIssue> => {
  const { data } = await api.patch(`/superadmin/lio-issues/${id}`, body);
  return data.issue;
};

/** Nominate the workspace whose employees fix Lio. "" clears it. */
export const setLioProductOrg = async (
  headOfficeId: string,
): Promise<Pick<LioIssueQueue, "product_org_id" | "product_org_name" | "assignees">> => {
  const { data } = await api.put("/superadmin/lio-issues/product-org", {
    head_office_id: headOfficeId,
  });
  return {
    product_org_id: data?.product_org_id ?? null,
    product_org_name: data?.product_org_name ?? null,
    assignees: data?.assignees || [],
  };
};
